"use client";

import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BellIcon, CheckIcon, CopyIcon, WalletIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { cardDigits, formatAmountUah, formatCardNumber } from "@/lib/collections";
import { openExternalUrl } from "@/lib/contacts";
import { canCreateCollection, canCreatePoll, canCreateSecretPoll } from "@/lib/roles";
import { givenInCase, guessGender } from "@/lib/child-grammar";
import { givenName } from "@/lib/names";
import type { CollectionDto, MeResponse, PollDto } from "@/lib/types";
import { cn } from "@/lib/utils";
import { StatusScreen } from "@/components/status-screen";
import { SmoothReveal } from "@/components/smooth-reveal";
import { useMeActions } from "@/components/me-provider";
import { useLiveReload } from "@/components/live-provider";

export function VotesBoard({ me, active = true }: { me: MeResponse; active?: boolean }) {
  const { refreshMe, markSeen } = useMeActions();
  const [polls, setPolls] = useState<PollDto[]>([]);
  const [collections, setCollections] = useState<CollectionDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [pollOpen, setPollOpen] = useState(false);
  const [fundOpen, setFundOpen] = useState(false);
  const [closedOpen, setClosedOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [options, setOptions] = useState("");
  const [multiple, setMultiple] = useState(false);
  const [anonymous, setAnonymous] = useState(false);
  const [secret, setSecret] = useState(false);
  const [fundTitle, setFundTitle] = useState("");
  const [fundDescription, setFundDescription] = useState("");
  const [fundAmount, setFundAmount] = useState("");
  const [fundPayTo, setFundPayTo] = useState("");
  const [fundPayLink, setFundPayLink] = useState("");
  const [remindingKey, setRemindingKey] = useState<string | null>(null);
  const [actingKey, setActingKey] = useState<string | null>(null);

  async function load() {
    const [votes, funds] = await Promise.all([
      api<{ polls: PollDto[] }>("/api/votes"),
      api<{ collections: CollectionDto[] }>("/api/collections"),
    ]);
    setPolls(votes.polls);
    setCollections(funds.collections);
    setLoading(false);
  }

  useEffect(() => {
    void load().catch((error) => {
      toast.error(error instanceof Error ? error.message : "Не вдалося завантажити голосування.");
      setLoading(false);
    });
  }, []);

  useLiveReload("votes", load, active);

  useEffect(() => {
    if (!active || loading) return;
    void markSeen("votes");
  }, [active, loading, polls, collections, markSeen]);

  async function vote(pollId: string, optionId: string, childId: string) {
    if (!childId) {
      toast.error("Спочатку прив’яжіть дитину у вкладці «Клас».");
      return;
    }
    const key = `${pollId}:${childId}:${optionId}`;
    setActingKey(key);
    try {
      await api(`/api/votes/${pollId}`, {
        method: "POST",
        body: JSON.stringify({ optionId, childId }),
      });
      await load();
      await refreshMe();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Не вдалося проголосувати.");
    } finally {
      setActingKey(null);
    }
  }

  async function togglePaid(collectionId: string, childId: string) {
    if (!childId) {
      toast.error("Спочатку прив’яжіть дитину у вкладці «Клас».");
      return;
    }
    const key = `pay:${collectionId}:${childId}`;
    setActingKey(key);
    try {
      await api(`/api/collections/${collectionId}`, {
        method: "POST",
        body: JSON.stringify({ childId }),
      });
      await load();
      await refreshMe();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Не вдалося позначити сплату.");
    } finally {
      setActingKey(null);
    }
  }

  /** One tap covers all of the parent's kids — no reason to pay in several transactions. */
  async function togglePaidForKids(collectionId: string, kids: Array<{ id: string; paid: boolean }>) {
    if (kids.length === 0) {
      toast.error("Спочатку прив’яжіть дитину у вкладці «Клас».");
      return;
    }
    const allPaid = kids.every((kid) => kid.paid);
    const targets = allPaid ? kids : kids.filter((kid) => !kid.paid);
    const key = `pay:${collectionId}:${kids.map((kid) => kid.id).join(",")}`;
    setActingKey(key);
    try {
      for (const kid of targets) {
        await api(`/api/collections/${collectionId}`, {
          method: "POST",
          body: JSON.stringify({ childId: kid.id }),
        });
      }
      await load();
      await refreshMe();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Не вдалося позначити сплату.");
    } finally {
      setActingKey(null);
    }
  }

  async function createPoll() {
    try {
      const result = await api<{
        id: string;
        delivered: number;
        wouldNotify: number;
        demo?: boolean;
      }>("/api/votes", {
        method: "POST",
        body: JSON.stringify({
          title,
          description,
          options: options.split("\n"),
          multiple,
          anonymous,
          secret: canCreateSecretPoll(me.user.role) ? secret : false,
        }),
      });
      setPollOpen(false);
      setTitle("");
      setDescription("");
      setOptions("");
      setMultiple(false);
      setAnonymous(false);
      setSecret(false);
      if (result.demo) {
        toast.success(
          secret
            ? `Секретне голосування відкрито. У демо бот мовчить (було б ${result.wouldNotify}).`
            : `Голосування відкрито. У демо бот мовчить (було б ${result.wouldNotify}).`,
        );
      } else if (result.delivered > 0) {
        toast.success(
          secret
            ? `Секретне голосування відкрито. Сповіщення: ${result.delivered}.`
            : `Голосування відкрито. Сповіщення надіслано ${result.delivered} батькам.`,
        );
      } else {
        toast.success(
          secret
            ? "Секретне голосування відкрито. Класний керівник його не побачить."
            : "Голосування відкрито. Результати побачать усі, хто має доступ.",
        );
      }
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Не вдалося створити.");
    }
  }

  async function createCollection() {
    try {
      const result = await api<{
        id: string;
        delivered: number;
        wouldNotify: number;
        demo?: boolean;
      }>("/api/collections", {
        method: "POST",
        body: JSON.stringify({
          title: fundTitle,
          description: fundDescription,
          amountUah: fundAmount,
          payTo: fundPayTo,
          payLink: fundPayLink,
        }),
      });
      setFundOpen(false);
      setFundTitle("");
      setFundDescription("");
      setFundAmount("");
      setFundPayTo("");
      setFundPayLink("");
      if (result.demo) {
        toast.success(`Збір відкрито. У демо бот мовчить (було б ${result.wouldNotify}).`);
      } else if (result.delivered > 0) {
        toast.success(`Збір відкрито. Сповіщення надіслано ${result.delivered} батькам.`);
      } else {
        toast.success("Збір відкрито. Батьки позначать сплату тут.");
      }
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Не вдалося створити збір.");
    }
  }

  async function togglePoll(poll: PollDto) {
    try {
      await api(`/api/votes/${poll.id}`, {
        method: "PATCH",
        body: JSON.stringify({ status: poll.status === "open" ? "closed" : "open" }),
      });
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Не вдалося змінити статус.");
    }
  }

  async function toggleCollection(collection: CollectionDto) {
    try {
      await api(`/api/collections/${collection.id}`, {
        method: "PATCH",
        body: JSON.stringify({ status: collection.status === "open" ? "closed" : "open" }),
      });
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Не вдалося змінити статус.");
    }
  }

  async function remindPoll(poll: PollDto) {
    setRemindingKey(`poll:${poll.id}`);
    try {
      const result = await api<{ silentCount: number; wouldNotify: number; demo?: boolean }>(
        `/api/votes/${poll.id}/remind`,
        { method: "POST" },
      );
      if (result.demo) {
        toast.success(`У демо бот мовчить. Без голосу: ${result.silentCount}.`);
      } else {
        toast.success(`Нагадування надіслано. Без голосу: ${result.silentCount}.`);
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Не вдалося нагадати.");
    } finally {
      setRemindingKey(null);
    }
  }

  async function remindCollection(collection: CollectionDto) {
    setRemindingKey(`fund:${collection.id}`);
    try {
      const result = await api<{ silentCount: number; wouldNotify: number; demo?: boolean }>(
        `/api/collections/${collection.id}/remind`,
        { method: "POST" },
      );
      if (result.demo) {
        toast.success(`У демо бот мовчить. Без сплати: ${result.silentCount}.`);
      } else {
        toast.success(`Нагадування надіслано. Без сплати: ${result.silentCount}.`);
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Не вдалося нагадати.");
    } finally {
      setRemindingKey(null);
    }
  }

  if (loading) {
    return <StatusScreen loading title="Голосування класу" text="Збори і короткі рішення без спаму в чаті." />;
  }

  const severalKids = me.children.length > 1;
  const feed = [
    ...collections.map((collection) => ({
      kind: "collection" as const,
      at: collection.createdAt,
      collection,
    })),
    ...polls.map((poll) => ({ kind: "poll" as const, at: poll.createdAt, poll })),
  ].sort((left, right) => right.at.localeCompare(left.at));
  const openFeed = feed.filter((item) =>
    item.kind === "collection" ? item.collection.status === "open" : item.poll.status === "open",
  );
  const closedFeed = feed.filter((item) =>
    item.kind === "collection" ? item.collection.status === "closed" : item.poll.status === "closed",
  );

  function renderFeedItem(item: (typeof feed)[number]) {
    return item.kind === "collection" ? (
      <CollectionCard
        key={item.collection.id}
        collection={item.collection}
        actingKey={actingKey}
        reminding={remindingKey === `fund:${item.collection.id}`}
        onTogglePaid={togglePaid}
        onTogglePaidForKids={togglePaidForKids}
        onRemind={() => void remindCollection(item.collection)}
        onToggle={() => void toggleCollection(item.collection)}
      />
    ) : (
      <PollCard
        key={item.poll.id}
        poll={item.poll}
        actingKey={actingKey}
        reminding={remindingKey === `poll:${item.poll.id}`}
        onVote={vote}
        onRemind={() => void remindPoll(item.poll)}
        onToggle={() => void togglePoll(item.poll)}
      />
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm leading-6 text-muted-foreground">
        {severalKids
          ? "Голос і сплата окремо за кожну дитину. Один може бути в школі, інший ні."
          : "Голос і сплата рахуються на дитину. Результати видно всім, хто бачить запис."}
      </p>

      {me.children.length === 0 ? (
        <p className="rounded-2xl bg-accent px-4 py-3 text-sm leading-6">
          Щоб голос або сплата зарахувались, адміністратор має прив’язати вас до дитини у «Клас».
        </p>
      ) : null}

      {canCreatePoll(me.user.role) || canCreateCollection(me.user.role) ? (
        <div className="grid gap-2">
          {canCreateCollection(me.user.role) ? (
            <Dialog open={fundOpen} onOpenChange={setFundOpen}>
              <DialogTrigger render={<Button className="h-11 w-full rounded-2xl" />}>
                Збір грошей
              </DialogTrigger>
              <DialogContent className="max-w-sm rounded-3xl">
                <DialogHeader>
                  <DialogTitle>На що збираємо?</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                  <Input
                    placeholder="Екскурсія, канцтовари, подарунок"
                    value={fundTitle}
                    onChange={(event) => setFundTitle(event.target.value)}
                  />
                  <Input
                    inputMode="numeric"
                    placeholder="Сума з дитини, грн"
                    value={fundAmount}
                    onChange={(event) => setFundAmount(event.target.value)}
                  />
                  <Input
                    inputMode="numeric"
                    autoComplete="off"
                    placeholder="Номер картки"
                    value={fundPayTo}
                    onChange={(event) => setFundPayTo(formatCardNumber(event.target.value))}
                  />
                  <Input
                    inputMode="url"
                    autoComplete="off"
                    placeholder="Посилання на банку"
                    value={fundPayLink}
                    onChange={(event) => setFundPayLink(event.target.value)}
                  />
                  <Textarea
                    placeholder="Коротко, навіщо ці гроші. Можна лишити порожнім."
                    value={fundDescription}
                    onChange={(event) => setFundDescription(event.target.value)}
                  />
                  <Button className="w-full rounded-2xl" onClick={() => void createCollection()}>
                    Оголосити збір
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          ) : null}

          {canCreatePoll(me.user.role) ? (
            <Dialog
              open={pollOpen}
              onOpenChange={(next) => {
                setPollOpen(next);
                if (!next) setSecret(false);
              }}
            >
              <DialogTrigger render={<Button className="h-11 w-full rounded-2xl" />}>
                Нове голосування
              </DialogTrigger>
              <DialogContent className="max-w-sm rounded-3xl">
                <DialogHeader>
                  <DialogTitle>Що вирішуємо?</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                  <Input
                    placeholder="Їдемо в суботу?"
                    value={title}
                    onChange={(event) => setTitle(event.target.value)}
                  />
                  <Textarea
                    placeholder="Коротко, що обираємо"
                    value={description}
                    onChange={(event) => setDescription(event.target.value)}
                  />
                  <Textarea
                    placeholder={"Кожен варіант з нового рядка.\nТак, їдемо\nНі, інший день"}
                    value={options}
                    onChange={(event) => setOptions(event.target.value)}
                    className="min-h-28"
                  />
                  <div className="flex flex-wrap gap-2">
                    <ToggleChip
                      on={multiple}
                      onClick={() => setMultiple((value) => !value)}
                      label={multiple ? "Кілька варіантів" : "Один варіант"}
                    />
                    <ToggleChip
                      on={anonymous}
                      onClick={() => setAnonymous((value) => !value)}
                      label={anonymous ? "Анонімно" : "З іменами"}
                    />
                    {canCreateSecretPoll(me.user.role) ? (
                      <ToggleChip
                        on={secret}
                        onClick={() => setSecret((value) => !value)}
                        label={secret ? "Секретно від класного керівника" : "Видно класному керівнику"}
                      />
                    ) : null}
                  </div>
                  <SmoothReveal open={secret && canCreateSecretPoll(me.user.role)}>
                    <p className="text-xs leading-5 text-muted-foreground">
                      Класний керівник не побачить тему, варіанти і хто як проголосував. Наприклад, що даруємо на День вчителя.
                    </p>
                  </SmoothReveal>
                  <Button className="w-full rounded-2xl" onClick={() => void createPoll()}>
                    Відкрити голосування
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          ) : null}
        </div>
      ) : null}

      {feed.length === 0 ? (
        <StatusScreen title="Поки тихо" text="Збір грошей — окремо: сума, картка і хто сплатив. Голосування — коли треба обрати варіант." />
      ) : (
        <div className="space-y-3">
          {openFeed.length > 0 ? <div className="wide-cards">{openFeed.map(renderFeedItem)}</div> : null}
          {closedFeed.length > 0 ? (
            <div className="space-y-2">
              <button
                type="button"
                className="block w-full text-center text-xs font-medium text-primary"
                onClick={() => setClosedOpen((value) => !value)}
                aria-expanded={closedOpen}
              >
                {closedOpen ? "Сховати закриті" : `Закриті (${closedFeed.length})`}
              </button>
              <SmoothReveal open={closedOpen}>
                <div className="wide-cards opacity-80">{closedFeed.map(renderFeedItem)}</div>
              </SmoothReveal>
            </div>
          ) : null}
          {openFeed.length === 0 && closedFeed.length > 0 && !closedOpen ? (
            <p className="text-center text-sm text-muted-foreground">Зараз відкритих зборів і голосувань немає.</p>
          ) : null}
        </div>
      )}
    </div>
  );
}

function PollCard({
  poll,
  actingKey,
  reminding,
  onVote,
  onRemind,
  onToggle,
}: {
  poll: PollDto;
  actingKey: string | null;
  reminding: boolean;
  onVote: (pollId: string, optionId: string, childId: string) => void;
  onRemind: () => void;
  onToggle: () => void;
}) {
  const max = Math.max(1, ...poll.options.map((option) => option.votes));
  const kids = poll.myChildren;
  return (
    <article className="rounded-3xl bg-card p-4 ring-1 ring-foreground/8">
      <div>
        <p className="text-xs font-medium tracking-wide text-primary uppercase">
          {poll.status === "open" ? "Відкрито" : "Закрито"} · {poll.totalVotes} з {poll.classSize}
          {poll.multiple ? " · кілька" : " · один"}
          {poll.anonymous ? " · анонімно" : " · з іменами"}
          {poll.secret ? " · секретно" : ""}
        </p>
        <h2 className="mt-1 text-base font-semibold">{poll.title}</h2>
        {poll.description ? (
          <p className="mt-1 text-sm leading-6 text-muted-foreground">{poll.description}</p>
        ) : null}
      </div>
      <div className="mt-3 space-y-3">
        {poll.options.map((option) => {
          const width = `${Math.round((option.votes / max) * 100)}%`;
          const onlyChild = kids.length === 1 ? kids[0] : null;
          const selectedByOnly = onlyChild?.optionIds.includes(option.id) ?? false;
          return (
            <div key={option.id} className="space-y-2">
              {onlyChild ? (
                <button
                  type="button"
                  disabled={poll.status !== "open" || actingKey !== null}
                  onClick={() => onVote(poll.id, option.id, onlyChild.id)}
                  className={cn(
                    "relative w-full overflow-hidden rounded-2xl border px-3 py-3 text-left text-sm font-medium",
                    selectedByOnly ? "border-primary bg-primary/8" : "border-transparent bg-accent",
                    poll.status !== "open" ? "opacity-80" : "active:scale-[0.99]",
                  )}
                >
                  <span className="pointer-events-none absolute inset-y-0 left-0 bg-primary/10" style={{ width }} />
                  <span className="relative flex items-center justify-between gap-2">
                    <span>{option.label}</span>
                    <span className="text-xs text-muted-foreground">{option.votes}</span>
                  </span>
                </button>
              ) : (
                <div className="relative overflow-hidden rounded-2xl bg-accent px-3 py-3 text-sm font-medium">
                  <span className="pointer-events-none absolute inset-y-0 left-0 bg-primary/10" style={{ width }} />
                  <span className="relative flex items-center justify-between gap-2">
                    <span>{option.label}</span>
                    <span className="text-xs text-muted-foreground">{option.votes}</span>
                  </span>
                </div>
              )}
              {kids.length > 1 ? (
                <div className="flex flex-wrap gap-2 px-0.5">
                  {kids.map((child) => {
                    const selected = child.optionIds.includes(option.id);
                    const key = `${poll.id}:${child.id}:${option.id}`;
                    return (
                      <button
                        key={child.id}
                        type="button"
                        disabled={poll.status !== "open" || actingKey !== null}
                        onClick={() => onVote(poll.id, option.id, child.id)}
                        className={cn(
                          "rounded-full px-3 py-1.5 text-xs font-medium",
                          selected
                            ? "bg-primary text-primary-foreground"
                            : "bg-accent text-foreground ring-1 ring-foreground/10",
                          poll.status !== "open" ? "opacity-70" : "",
                        )}
                      >
                        {actingKey === key ? "…" : givenName(child.name)}
                      </button>
                    );
                  })}
                </div>
              ) : null}
              {option.voters.length > 0 ? (
                <p className="px-1 text-xs leading-5 text-muted-foreground">{option.voters.join(", ")}</p>
              ) : null}
            </div>
          );
        })}
      </div>
      {poll.canManage ? (
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
          {poll.status === "open" && poll.silentCount > 0 ? (
            <button
              type="button"
              disabled={reminding}
              onClick={onRemind}
              className="inline-flex items-center gap-1 text-xs font-medium text-primary"
            >
              <BellIcon className="size-3.5" />
              {reminding ? "Надсилаємо…" : `Нагадати, хто не проголосував (${poll.silentCount})`}
            </button>
          ) : null}
          <button type="button" onClick={onToggle} className="text-xs font-medium text-primary">
            {poll.status === "open" ? "Закрити голосування" : "Відкрити знову"}
          </button>
        </div>
      ) : null}
    </article>
  );
}

function CollapsiblePaidNames({ names }: { names: string[] }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="px-1">
      <button type="button" className="text-xs font-medium text-primary" onClick={() => setOpen((v) => !v)}>
        {open ? "Сховати" : `Хто сплатив (${names.length})`}
      </button>
      <SmoothReveal open={open}>
        <p className="mt-1 text-xs leading-5 text-muted-foreground">{names.join(", ")}</p>
      </SmoothReveal>
    </div>
  );
}

function payButtonLabel(kids: Array<{ name: string; paid: boolean }>, acting: boolean) {
  const allPaid = kids.every((kid) => kid.paid);
  const icon = allPaid ? <CheckIcon className="size-4" /> : <WalletIcon className="size-4" />;
  if (acting) return icon;
  if (kids.length === 1) return (
    <>
      {icon}
      {allPaid ? "Сплачено" : "Ми вже сплатили"}
    </>
  );
  const names = kids.map((kid) => givenInCase(kid.name, guessGender(kid.name), "acc"));
  const joined = names.length > 1 ? `${names.slice(0, -1).join(", ")} і ${names.at(-1)}` : names[0];
  return (
    <>
      {icon}
      {allPaid ? `Сплачено за ${joined}` : `Сплатили за ${joined}`}
    </>
  );
}

function CollectionCard({
  collection,
  actingKey,
  reminding,
  onTogglePaid,
  onTogglePaidForKids,
  onRemind,
  onToggle,
}: {
  collection: CollectionDto;
  actingKey: string | null;
  reminding: boolean;
  onTogglePaid: (collectionId: string, childId: string) => void;
  onTogglePaidForKids: (collectionId: string, kids: Array<{ id: string; paid: boolean }>) => void;
  onRemind: () => void;
  onToggle: () => void;
}) {
  const [rosterOpen, setRosterOpen] = useState(false);
  const width = `${Math.round((collection.paidCount / Math.max(1, collection.classSize)) * 100)}%`;
  const ownKids = collection.myChildren;
  const ownKey = `pay:${collection.id}:${ownKids.map((kid) => kid.id).join(",")}`;

  return (
    <article className="rounded-3xl bg-card p-4 ring-1 ring-foreground/8">
      <div>
        <p className="text-xs font-medium tracking-wide text-primary uppercase">
          {collection.status === "open" ? "Відкрито" : "Закрито"} · {collection.paidCount} з {collection.classSize}
          {" · збір"}
        </p>
        <h2 className="mt-1 text-base font-semibold">{collection.title}</h2>
        {collection.amountUah ? (
          <p className="mt-1 text-sm font-medium text-primary">{formatAmountUah(collection.amountUah)} з дитини</p>
        ) : null}
        {collection.description ? (
          <p className="mt-1 text-sm leading-6 text-muted-foreground">{collection.description}</p>
        ) : null}
      </div>

      {collection.payTo ? (
        <button
          type="button"
          onClick={() => void copyText(cardDigits(collection.payTo) || collection.payTo)}
          className="mt-3 flex w-full items-center justify-between gap-3 rounded-2xl bg-accent px-3 py-3 text-left"
          aria-label="Копіювати номер картки"
        >
          <span className="min-w-0">
            <span className="block text-[11px] font-medium tracking-wide text-muted-foreground uppercase">
              Картка
            </span>
            <span className="mt-0.5 block text-base font-semibold tabular-nums tracking-wide select-all">
              {formatCardNumber(collection.payTo) || collection.payTo}
            </span>
          </span>
          <span className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-primary">
            <CopyIcon className="size-3.5" />
            Копіювати
          </span>
        </button>
      ) : null}

      {collection.payLink ? (
        <button
          type="button"
          onClick={() => openExternalUrl(collection.payLink)}
          className="mt-2 flex w-full items-center justify-between gap-3 rounded-2xl border border-primary/20 bg-primary/8 px-3 py-3 text-left"
        >
          <span className="min-w-0">
            <span className="block text-[11px] font-medium tracking-wide text-primary uppercase">Банка</span>
            <span className="mt-0.5 block truncate text-sm font-medium text-foreground">Відкрити посилання</span>
          </span>
          <span className="shrink-0 text-xs font-medium text-primary">Перейти</span>
        </button>
      ) : null}

      <div className="mt-3 space-y-2">
        <div
          className={cn(
            "relative overflow-hidden rounded-2xl border px-3 py-3 text-sm font-medium",
            collection.paidCount > 0 ? "border-primary bg-primary/8" : "border-transparent bg-accent",
          )}
        >
          <span className="pointer-events-none absolute inset-y-0 left-0 bg-primary/10" style={{ width }} />
          <span className="relative flex items-center justify-between gap-2">
            <span>Сплатили</span>
            <span className="text-xs text-muted-foreground">
              {collection.paidCount} з {collection.classSize}
            </span>
          </span>
        </div>

        {!collection.canManage && ownKids.length > 0 ? (
          <Button
            type="button"
            disabled={collection.status !== "open" || actingKey !== null}
            aria-pressed={ownKids.every((kid) => kid.paid)}
            className="h-11 w-full gap-2 rounded-2xl"
            onClick={() => onTogglePaidForKids(collection.id, ownKids)}
          >
            {payButtonLabel(ownKids, actingKey === ownKey)}
          </Button>
        ) : null}

        {collection.paidNames.length > 0 && !collection.canManage ? (
          <CollapsiblePaidNames names={collection.paidNames} />
        ) : null}

        {collection.canManage && collection.roster.length > 0 ? (
          <div className="px-1">
            <button
              type="button"
              className="text-xs font-medium text-primary"
              onClick={() => setRosterOpen((open) => !open)}
            >
              {rosterOpen ? "Сховати список класу" : `Список класу (${collection.roster.length})`}
            </button>
            <SmoothReveal open={rosterOpen}>
              <div className="mt-2 flex flex-wrap gap-2">
                {collection.roster.map((child) => {
                  const key = `pay:${collection.id}:${child.id}`;
                  return (
                    <button
                      key={child.id}
                      type="button"
                      disabled={collection.status !== "open" || actingKey !== null}
                      onClick={() => onTogglePaid(collection.id, child.id)}
                      className={cn(
                        "rounded-full px-3 py-1.5 text-xs font-medium",
                        child.paid
                          ? "bg-primary text-primary-foreground"
                          : "bg-accent text-foreground ring-1 ring-foreground/10",
                        collection.status !== "open" ? "opacity-70" : "",
                      )}
                    >
                      {actingKey === key ? "…" : child.name}
                    </button>
                  );
                })}
              </div>
            </SmoothReveal>
          </div>
        ) : null}
      </div>

      {collection.canManage ? (
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
          {collection.status === "open" && collection.unpaidCount > 0 ? (
            <button
              type="button"
              disabled={reminding}
              onClick={onRemind}
              className="inline-flex items-center gap-1 text-xs font-medium text-primary"
            >
              <BellIcon className="size-3.5" />
              {reminding ? "Надсилаємо…" : `Нагадати, хто не сплатив (${collection.unpaidCount})`}
            </button>
          ) : null}
          <button type="button" onClick={onToggle} className="text-xs font-medium text-primary">
            {collection.status === "open" ? "Закрити збір" : "Відкрити знову"}
          </button>
        </div>
      ) : null}
    </article>
  );
}

async function copyText(value: string) {
  try {
    await navigator.clipboard.writeText(value);
    toast.success("Скопійовано");
  } catch {
    toast.message(value);
  }
}

function ToggleChip({
  on,
  label,
  onClick,
}: {
  on: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-full px-3 py-1.5 text-xs font-medium",
        on ? "bg-primary text-primary-foreground" : "bg-accent text-foreground",
      )}
    >
      {label}
    </button>
  );
}
